import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Box
} from '@mui/material';
import { OutlierConfigType } from '../../../../../services/ConfigurationService/types/centerType';

interface AddEditOutlierConfigDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (config: OutlierConfigType) => void;
  onDelete?: (config: OutlierConfigType) => void;
  editingConfig?: OutlierConfigType | null;
}

interface OutlierConfigFormData {
  id: string;
  effectiveDate: string;
  hospitalMultipleThreshold: number | null;
  fixedDollarThreshold: number | null;
  outlierPaymentPercentage: number | null;
  costToChargeRatio: number | null;
}

const INITIAL_FORM_DATA: OutlierConfigFormData = {
  id: '',
  effectiveDate: new Date().toISOString().split('T')[0],
  hospitalMultipleThreshold: null,
  fixedDollarThreshold: null,
  outlierPaymentPercentage: null,
  costToChargeRatio: null
};

const NUMERIC_FIELDS = [
  'hospitalMultipleThreshold',
  'fixedDollarThreshold',
  'outlierPaymentPercentage',
  'costToChargeRatio'
];

export function AddEditOutlierConfigDialog({
  open,
  onClose,
  onSave,
  onDelete,
  editingConfig
}: AddEditOutlierConfigDialogProps) {
  const [form, setForm] = useState<OutlierConfigFormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const isEditMode = !!editingConfig;

  useEffect(() => {
    if (editingConfig) {
      // Parse date directly to avoid UTC timezone shift
      const dateValue = editingConfig.effectiveDate
        ? editingConfig.effectiveDate.split('T')[0]
        : '';
      setForm({
        id: editingConfig.id,
        effectiveDate: dateValue,
        hospitalMultipleThreshold: editingConfig.hospitalMultipleThreshold,
        fixedDollarThreshold: editingConfig.fixedDollarThreshold,
        outlierPaymentPercentage: editingConfig.outlierPaymentPercentage,
        costToChargeRatio: editingConfig.costToChargeRatio
      });
    } else {
      setForm(INITIAL_FORM_DATA);
    }
    setErrors({});
  }, [editingConfig, open]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const isNumericField = NUMERIC_FIELDS.includes(name);

    let processedValue: string | number | null = value;

    if (isNumericField) {
      if (value === '' || value === null) {
        processedValue = null;
      } else {
        processedValue = parseFloat(value) || null;
      }
    }

    setForm(prevForm => ({
      ...prevForm,
      [name]: processedValue
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [errors]);

  const validateForm = useCallback((): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!form.effectiveDate) {
      newErrors.effectiveDate = 'Effective date is required';
    }

    if (form.hospitalMultipleThreshold === null) {
      newErrors.hospitalMultipleThreshold = 'Hospital multiple threshold is required';
    }

    if (form.fixedDollarThreshold === null) {
      newErrors.fixedDollarThreshold = 'Fixed dollar threshold is required';
    }

    if (form.outlierPaymentPercentage === null) {
      newErrors.outlierPaymentPercentage = 'Outlier payment percentage is required';
    }

    if (form.costToChargeRatio === null) {
      newErrors.costToChargeRatio = 'Cost-to-charge ratio is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [form]);

  const handleSave = () => {
    if (validateForm()) {
      const configToSave: OutlierConfigType = {
        id: form.id || crypto.randomUUID(),
        effectiveDate: form.effectiveDate + 'T00:00:00',
        hospitalMultipleThreshold: form.hospitalMultipleThreshold ?? 0,
        fixedDollarThreshold: form.fixedDollarThreshold ?? 0,
        outlierPaymentPercentage: form.outlierPaymentPercentage ?? 0,
        costToChargeRatio: form.costToChargeRatio ?? 0
      };

      onSave(configToSave);
      onClose();
    }
  };

  const handleDelete = () => {
    if (editingConfig && onDelete) {
      onDelete(editingConfig);
      onClose();
    }
  };

  const handleClose = () => {
    setForm(INITIAL_FORM_DATA);
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditMode ? 'Edit Outlier Configuration' : 'Add Outlier Configuration'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} mt={1}>
          <TextField
            label="Effective Date"
            name="effectiveDate"
            type="date"
            value={form.effectiveDate}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.effectiveDate}
            helperText={errors.effectiveDate}
            InputLabelProps={{ shrink: true }}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label="Hospital Multiple Threshold"
              name="hospitalMultipleThreshold"
              type="number"
              value={form.hospitalMultipleThreshold ?? ''}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.hospitalMultipleThreshold}
              helperText={errors.hospitalMultipleThreshold}
              inputProps={{ step: 0.0001, min: 0 }}
            />
            <TextField
              label="Fixed Dollar Threshold"
              name="fixedDollarThreshold"
              type="number"
              value={form.fixedDollarThreshold ?? ''}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.fixedDollarThreshold}
              helperText={errors.fixedDollarThreshold}
              inputProps={{ step: 0.01, min: 0 }}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Outlier Payment %"
              name="outlierPaymentPercentage"
              type="number"
              value={form.outlierPaymentPercentage ?? ''}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.outlierPaymentPercentage}
              helperText={errors.outlierPaymentPercentage}
              inputProps={{ step: 0.0001, min: 0, max: 1 }}
            />
            <TextField
              label="Cost-to-Charge Ratio"
              name="costToChargeRatio"
              type="number"
              value={form.costToChargeRatio ?? ''}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.costToChargeRatio}
              helperText={errors.costToChargeRatio}
              inputProps={{ step: 0.0001, min: 0 }}
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Box>
            {isEditMode && onDelete && (
              <Button onClick={handleDelete} color="error" variant="outlined">
                Delete
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleSave} variant="contained">
              {isEditMode ? 'Update' : 'Add'}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
